import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AutocompleteInput = ({ 
  field, 
  value, 
  onChange, 
  onSelect,  // New callback for when a suggestion is selected
  placeholder = "", 
  className = "",
  // When "blexbot", hit the backend /blexbot/products proxy that queries
  // the authenticated Blexbot catalog in real time. When empty/undefined
  // falls back to the local /autocomplete/{field} suggestions.
  source = ""
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Debounce function to limit API calls
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  // Fetch suggestions from API
  const fetchSuggestions = async (query) => {
    if (!query || query.length < 1) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const url =
        source === "blexbot"
          ? `${API}/blexbot/products`
          : `${API}/autocomplete/${field}`;
      const params = source === "blexbot" ? { q: query, limit: 10 } : { query };
      const response = await axios.get(url, { params });
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error("Autocomplete error:", error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Debounced version of fetchSuggestions
  const debouncedFetchSuggestions = debounce(fetchSuggestions, 300);

  // Handle input change
  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    onChange(inputValue);
    
    if (inputValue.length > 0) {
      debouncedFetchSuggestions(inputValue);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Handle suggestion selection
  const handleSuggestionClick = (suggestion) => {
    // If suggestion is an object, extract the value
    const value = typeof suggestion === 'object' ? suggestion.value : suggestion;
    onChange(value);
    
    // Call onSelect callback if provided (passes full product data)
    if (onSelect && typeof suggestion === 'object') {
      onSelect(suggestion);
    }
    
    setShowSuggestions(false);
    setSuggestions([]);
  };

  // Handle input focus
  const handleFocus = () => {
    if (value && suggestions.length > 0) {
      setShowSuggestions(true);
    } else if (value) {
      fetchSuggestions(value);
      setShowSuggestions(true);
    }
  };

  // Handle input blur with delay to allow clicking suggestions
  const handleBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 150);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputRef.current && 
        !inputRef.current.contains(event.target) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={`relative ${className}`}>
      <Input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoComplete="off"
      />
      
      {/* Loading indicator */}
      {isLoading && (
        <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
        </div>
      )}
      
      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
        >
          {suggestions.map((suggestion, index) => {
            const isObject = typeof suggestion === 'object';
            const displayValue = isObject ? suggestion.value : suggestion;
            // Prefer Blexbot `imagen` field, fall back to legacy local image_base64.
            const imgSrc = isObject ? (suggestion.imagen || suggestion.image_base64 || "") : "";
            // Blexbot shape has nombre/marca/modelo/precio; local shape has maker/model.
            const subMarca = isObject ? (suggestion.marca || suggestion.maker || "") : "";
            const subModelo = isObject ? (suggestion.modelo || suggestion.model || "") : "";
            const subPrecio = isObject ? suggestion.precio : null;
            const subRef = isObject ? (suggestion.referencia || "") : "";
            return (
              <div
                key={index}
                onClick={() => handleSuggestionClick(suggestion)}
                data-testid={`autocomplete-suggestion-${index}`}
                className="px-3 py-2 cursor-pointer hover:bg-blue-50 hover:text-blue-700 text-sm border-b border-gray-100 last:border-b-0 flex items-center space-x-3"
              >
                {imgSrc && (
                  <img
                    src={imgSrc}
                    alt={displayValue}
                    className="w-10 h-10 object-cover rounded border border-gray-300"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{displayValue}</div>
                  {isObject && (
                    <div className="text-xs text-gray-500 truncate">
                      {[subMarca, subModelo, subRef && `Ref ${subRef}`]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  )}
                </div>
                {subPrecio ? (
                  <div className="text-xs font-semibold text-green-700 whitespace-nowrap">
                    ${Number(subPrecio).toLocaleString()}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
      
      {/* No suggestions message */}
      {showSuggestions && !isLoading && suggestions.length === 0 && value.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
          <div className="px-3 py-2 text-sm text-gray-500">
            No suggestions found
          </div>
        </div>
      )}
    </div>
  );
};

export default AutocompleteInput;